import { ResourceStock } from '../systems/ResourceManager';

// === RECETTES DE CRAFT ===
// Chaque recette nécessite un niveau d'atelier minimum et des ressources.
// Le résultat est soit un item (équipement), soit un consommable.

export type CraftResultType = 'item' | 'consumable';

export interface CraftRecipe {
  id: string;
  name: string;
  emoji: string;
  description: string;
  /** Niveau d'atelier requis (1, 2 ou 3) */
  workshopLevel: number;
  /** Coût en ressources */
  cost: Partial<ResourceStock>;
  /** Type de résultat */
  resultType: CraftResultType;
  /** ID de l'item ou consommable produit */
  resultId: string;
  /** Quantité produite (1 par défaut) */
  resultAmount?: number;
}

export const CRAFT_RECIPES: CraftRecipe[] = [

  // === ATELIER NIVEAU 1 — Recettes basiques ===

  {
    id: 'craft_health_potion',
    name: 'Potion de Rhum',
    emoji: '🍺',
    description: 'Brasse un remède pirate. Soigne 8 HP.',
    workshopLevel: 1,
    cost: { cloth: 2 },
    resultType: 'consumable',
    resultId: 'health_potion',
  },
  {
    id: 'craft_bandage',
    name: 'Bandage',
    emoji: '🩹',
    description: 'Pansement de fortune. Soigne 6 HP. Mini-soin économique.',
    workshopLevel: 1,
    cost: { cloth: 1 },
    resultType: 'consumable',
    resultId: 'bandage',
  },
  {
    id: 'craft_grenade',
    name: 'Bombe à Poudre',
    emoji: '💣',
    description: 'Explosif artisanal. 6 dégâts en zone.',
    workshopLevel: 1,
    cost: { iron: 2, wood: 2 },
    resultType: 'consumable',
    resultId: 'grenade',
  },

  // === ATELIER NIVEAU 2 — Recettes avancées ===

  {
    id: 'craft_improved_blade',
    name: 'Lame Améliorée',
    emoji: '⚔️',
    description: 'Lame forgée avec soin. +3 ATK.',
    workshopLevel: 2,
    cost: { iron: 5, crystal: 2 },
    resultType: 'item',
    resultId: 'improved_blade',
  },
  {
    id: 'craft_reinforced_armor',
    name: 'Armure Renforcée',
    emoji: '🛡️',
    description: 'Plaques rivetées. +2 DEF, +4 HP.',
    workshopLevel: 2,
    cost: { iron: 4, cloth: 3, crystal: 1 },
    resultType: 'item',
    resultId: 'reinforced_armor',
  },
  {
    id: 'craft_voodoo_elixir',
    name: 'Élixir Vaudou',
    emoji: '🧪',
    description: 'Potion puissante. Soigne 15 HP.',
    workshopLevel: 2,
    cost: { cloth: 3, crystal: 2 },
    resultType: 'consumable',
    resultId: 'big_health_potion',
  },

  // === ATELIER NIVEAU 3 — Recettes légendaires ===

  {
    id: 'craft_cursed_sabre',
    name: 'Sabre Maudit',
    emoji: '🗡️',
    description: 'Lame imprégnée de malédiction. +4 ATK, +1 PA.',
    workshopLevel: 3,
    cost: { iron: 5, crystal: 3, cursed_bone: 2 },
    resultType: 'item',
    resultId: 'cursed_sabre',
  },
  {
    id: 'craft_spectral_cape',
    name: 'Cape Spectrale',
    emoji: '👻',
    description: 'Tissée d\'ombres. +2 DEF, +2 PM.',
    workshopLevel: 3,
    cost: { cloth: 5, cursed_bone: 2 },
    resultType: 'item',
    resultId: 'spectral_cape',
  },
  {
    id: 'craft_abyss_ring',
    name: 'Anneau d\'Abîme',
    emoji: '🌀',
    description: 'Puissance des profondeurs. +2 ATK, +1 PA, +3 HP.',
    workshopLevel: 3,
    cost: { crystal: 4, cursed_bone: 3 },
    resultType: 'item',
    resultId: 'abyss_ring',
  },
];

/** Récupère les recettes disponibles pour un niveau d'atelier donné */
export function getAvailableRecipes(workshopLevel: number): CraftRecipe[] {
  return CRAFT_RECIPES.filter(r => r.workshopLevel <= workshopLevel);
}
